import PremioIcon from "../../../../icons/menu/premio";
import PedidoIcon from "../../../../icons/menu/pedido";
import ProductoIcon from "../../../../icons/menu/producto";
import HomeIcon from "../../../../icons/menu/home";
import MarcaIcon from "../../../../icons/menu/marca";
import UsuarioIcon from "../../../../icons/menu/perfil";
import AyudaIcon from "../../../../icons/menu/ayuda";
import TerminosIcon from "../../../../icons/menu/terminos";
import { LogOut, Menu as MenuOpenIcon, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

interface Props {
  view: string;
  setView: React.Dispatch<React.SetStateAction<string>>;
}

type Item = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

const UsuarioMenu = ({ view, setView }: Props) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const closeDrawer = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeDrawer();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, closeDrawer]);

  const handleLogout = () => {
    closeDrawer();
    Swal.fire({
      title: "Estas seguro que quieres salir?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#205f43",
      cancelButtonColor: "#d33",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Salir!",
    }).then((result) => {
      if (result.isConfirmed) {
        sessionStorage.clear();
        navigate("/");
      }
    });
  };

  const items: Item[] = [
    { id: "Home", label: "Inicio", icon: <HomeIcon /> },
    { id: "Premios", label: "Premios", icon: <PremioIcon /> },
    { id: "Pedidos", label: "Pedidos", icon: <PedidoIcon /> },
    { id: "Marca", label: "Marca", icon: <MarcaIcon /> },
    { id: "Productos", label: "Productos", icon: <ProductoIcon /> },
    { id: "Perfil", label: "Perfil", icon: <UsuarioIcon /> },
    { id: "Ayuda", label: "Ayuda", icon: <AyudaIcon /> },
    { id: "Terminos", label: "Términos", icon: <TerminosIcon /> },
  ];

  const navItem = (item: Item, isMobile: boolean) => {
    const active = view === item.id;
    return (
      <button
        type="button"
        key={item.id}
        onClick={() => {
          setView(item.id);
          if (isMobile) closeDrawer();
        }}
        className={
          isMobile
            ? `flex w-full items-center gap-3 rounded-2xl px-3 py-3 text-left text-sm font-medium transition ${
                active
                  ? "bg-[#205f43]/12 text-[#205f43] shadow-sm"
                  : "text-gray-800 hover:bg-gray-100"
              }`
            : `flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left text-sm ${
                active
                  ? "bg-[#205f43]/10 font-medium text-[#205f43]"
                  : "text-gray-700 hover:bg-gray-100/80"
              }`
        }
      >
        <span
          className={
            isMobile
              ? `flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                  active
                    ? "bg-[#205f43]/18 text-[#1a4d32]"
                    : "bg-zinc-200/90 text-zinc-900"
                } [&>svg]:h-5 [&>svg]:w-5`
              : `flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
                  active
                    ? "bg-[#205f43]/18 text-[#1a4d32]"
                    : "bg-zinc-200/80 text-zinc-900"
                } [&>svg]:h-[18px] [&>svg]:w-[18px]`
          }
        >
          {item.icon}
        </span>
        {item.label}
      </button>
    );
  };

  const salirButton = (isMobile: boolean) => (
    <button
      type="button"
      onClick={handleLogout}
      className={
        isMobile
          ? "mt-2 flex w-full items-center gap-3 rounded-2xl border border-red-200/80 bg-red-50/80 px-3 py-3 text-left text-sm font-medium text-red-700 transition hover:bg-red-100"
          : "mt-1 flex w-full items-center gap-3 rounded-xl border border-red-200/60 bg-red-50/50 px-2 py-2 text-left text-sm font-medium text-red-700 hover:bg-red-50"
      }
    >
      <span
        className={
          isMobile
            ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100"
            : "flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100/80"
        }
      >
        <LogOut className="h-5 w-5 text-red-600" />
      </span>
      Cerrar sesión
    </button>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col">
      {/* Mobile: top bar + slide drawer */}
      <div className="w-full shrink-0 md:hidden">
        <div
          className="flex h-14 items-center justify-between gap-3 border-b border-gray-200/80 bg-white/95 px-3 backdrop-blur"
        >
          <div className="min-w-0 pl-0.5">
            <img
              src={require("../../../../icons/Logo-Celmar.png")}
              alt="Celmar"
              className="h-8 w-auto"
            />
          </div>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition hover:bg-[#205f43]/10 hover:text-[#205f43]"
            aria-label="Abrir menú"
          >
            <MenuOpenIcon className="h-6 w-6" strokeWidth={2.2} />
          </button>
        </div>

        {/* Overlay + drawer */}
        <div
          className={`fixed inset-0 z-[60] transition-opacity duration-200 ${
            open ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
          }`}
        >
          <div
            className="absolute inset-0 bg-black/45 backdrop-blur-[2px]"
            onClick={closeDrawer}
            aria-hidden="true"
          />
          <aside
            className={`absolute top-0 left-0 z-[1] flex h-full w-[min(20rem,100%)] max-w-[min(20rem,92vw)] flex-col bg-white shadow-2xl transition-transform duration-300 ease-out ${
              open ? "translate-x-0" : "-translate-x-full"
            }`}
            aria-hidden={!open}
            aria-label="Navegación"
          >
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-100 px-3">
              <img
                src={require("../../../../icons/Logo-Celmar.png")}
                alt=""
                className="h-7 w-auto"
              />
              <button
                type="button"
                onClick={closeDrawer}
                className="inline-flex h-9 w-9 items-center justify-center rounded-full text-gray-600 transition hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto px-3 py-3">
              {items.map((i) => navItem(i, true))}
            </nav>
            <div className="shrink-0 border-t border-gray-100 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              {salirButton(true)}
            </div>
          </aside>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden min-h-0 w-full min-w-0 flex-1 flex-col border-r border-gray-200/90 bg-gradient-to-b from-white to-gray-50/80 md:flex">
        <div className="shrink-0 border-b border-gray-200/60 px-3 py-4">
          <div className="mx-auto w-fit max-w-full">
            <img
              src={require("../../../../icons/Logo-Celmar.png")}
              alt="Celmar"
              className="max-h-12 w-auto object-contain"
            />
          </div>
        </div>
        <nav className="min-h-0 flex-1 space-y-0.5 overflow-y-auto p-2">
          {items.map((i) => navItem(i, false))}
        </nav>
        <div className="shrink-0 border-t border-gray-200/60 p-2">
          {salirButton(false)}
        </div>
      </div>
    </div>
  );
};

export default UsuarioMenu;
